/**
 * Single source of truth for which portal paths are served to anonymous
 * visitors. Referenced by the router guard, the analytics bootstrap and the
 * public pages themselves, so "public" cannot mean three different things.
 *
 * `index.html` cannot import this module and repeats the prefix checks as
 * string comparisons for the GA4 Consent Mode defaults — keep the two in sync.
 */
export const PUBLIC_PROMO_PATH_PREFIX = '/promo/';
export const PUBLIC_CHECKOUT_PATH_PREFIX = '/checkout/';

function currentPath(path?: string): string {
  return path ?? (typeof window !== 'undefined' ? window.location.pathname : '');
}

/**
 * Public co-branded voucher landing pages (`/promo/:slug`).
 *
 * These are consumer pages shown without a cookie banner, which has two
 * consequences beyond skipping the auth guard: no device storage may be
 * written, and nothing may read the portal session. Anything gated on this
 * predicate is one of those two rules.
 *
 * Mirrored server-side by `isPublicEndpoint()` in rp-users.
 */
export function isPublicPromoPath(path?: string): boolean {
  return currentPath(path).startsWith(PUBLIC_PROMO_PATH_PREFIX);
}

/**
 * Public fleet checkout (`/checkout/fleet`, D-01).
 *
 * Unlike promo it is tracked completely by GA4 (D-32: legal cleared full
 * analytics tracking, so analytics cookies are allowed; ads storage stays
 * denied), and it keeps one functional item, the session reference with its
 * attribution entry, under `localStorage['ryd.fleetCheckout.session']`
 * (D-04, D-28). No form value is ever written to the device.
 *
 * Mirrored server-side by `isPublicEndpoint()` in rp-users (`/fleet-checkout/v1/`).
 */
export function isPublicCheckoutPath(path?: string): boolean {
  return currentPath(path).startsWith(PUBLIC_CHECKOUT_PATH_PREFIX);
}

/** Every path rendered without a portal session. */
export function isPublicPath(path?: string): boolean {
  return isPublicPromoPath(path) || isPublicCheckoutPath(path);
}
